import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import FilterPanel from '../../components/ui/FilterPanel';
import MetricCard from './components/MetricCard';
import CallVolumeChart from './components/CallVolumeChart';
import PipelineFunnel from './components/PipelineFunnel';
import EngagementHeatmap from './components/EngagementHeatmap';
import QuickActions from './components/QuickActions';
import Icon from '../../components/AppIcon';
import { fetchCalls } from '../../api/calls';
import { fetchProspectStats, apiFetch } from '../../api/prospects';
import { useNavigate } from 'react-router-dom';
import { useCompaniesOptional } from '../../context/CompanyContext';

const SalesOverviewDashboard = () => {
  const navigate = useNavigate();
  const companyCx = useCompaniesOptional();
  const activeCompany = useMemo(() => {
    const list = companyCx?.companies ?? [];
    const id = String(companyCx?.activeCompanyId ?? '');
    return list.find((c) => String(c._id || c.id) === id) ?? null;
  }, [companyCx?.companies, companyCx?.activeCompanyId]);
  const mailRemaining = Math.max(0, Number(activeCompany?.quotaMail ?? 0));
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [kpiData, setKpiData] = useState([]);
  const [callVolumeData, setCallVolumeData] = useState([]);
  const [pipelineStages, setPipelineStages] = useState([]);
  const [engagementData, setEngagementData] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const now = new Date();
        const from = new Date();
        from.setDate(from.getDate() - 7);

        const [callsRes, prospectsStatsRes] = await Promise.all([
          fetchCalls({ from, to: now }),
          fetchProspectStats(),
        ]);

        const calls = callsRes?.data?.calls || [];
        const statsByStatus = prospectsStatsRes?.data?.stats || {};

        // --- KPIs ---
        const totalCalls = calls.length;
        const totalDuration = calls.reduce((acc, c) => acc + (c.timeDuration || 0), 0);
        const avgDurationSec = totalCalls ? totalDuration / totalCalls : 0;
        // Conversion globale basée sur les deals closés (prospects "conclu")
        const closedDeals = statsByStatus.conclu || 0;
        const totalPipelineForConversion =
          (statsByStatus.prospect || 0) +
          (statsByStatus.qualifie || 0) +
          (statsByStatus.proposition || 0) +
          (statsByStatus.negociation || 0) +
          (statsByStatus.conclu || 0);
        const conversionRate = totalPipelineForConversion
          ? (closedDeals / totalPipelineForConversion) * 100
          : 0;

        // Prospects actifs = tous les prospects sauf "non_interesse"
        const totalProspectsCount =
          (statsByStatus.prospect || 0) +
          (statsByStatus.qualifie || 0) +
          (statsByStatus.proposition || 0) +
          (statsByStatus.negociation || 0) +
          (statsByStatus.conclu || 0) +
          (statsByStatus.non_interesse || 0);
        const activeProspects =
          totalProspectsCount - (statsByStatus.non_interesse || 0);

        const formatNumber = (n) =>
          n.toLocaleString('fr-FR', { maximumFractionDigits: 1 });

        const formatDuration = (seconds) => {
          const mins = Math.floor(seconds / 60);
          const secs = Math.round(seconds % 60);
          return `${mins}m ${secs.toString().padStart(2, '0')}s`;
        };

        setKpiData([
          {
            title: 'Total des appels (7 jours)',
            value: formatNumber(totalCalls),
            change: '',
            changeType: 'neutral',
            icon: 'Phone',
            trend: [],
          },
          {
            title: 'Taux de conversion (deals closés)',
            value: `${formatNumber(conversionRate)}%`,
            change: '',
            changeType: 'neutral',
            icon: 'TrendingUp',
            trend: [],
          },
          {
            title: 'Prospects actifs',
            value: formatNumber(activeProspects),
            change: '',
            changeType: 'neutral',
            icon: 'Users',
            trend: [],
          },
        ]);

        // --- Volume d'appels par jour ---
        const days = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
        const dayBuckets = {};
        days.forEach((d) => {
          dayBuckets[d] = { day: d, calls: 0, conversionRate: 0, success: 0 };
        });

        calls.forEach((c) => {
          const d = new Date(c.startCallAt);
          const dayIndex = d.getDay(); // 0 = Dimanche
          const label = days[dayIndex];
          dayBuckets[label].calls += 1;
          if (c.status === 'success') {
            dayBuckets[label].success += 1;
          }
        });

        const callVolume = days.map((d) => {
          const bucket = dayBuckets[d];
          const rate = bucket.calls
            ? (bucket.success / bucket.calls) * 100
            : 0;
          return {
            day: d,
            calls: bucket.calls,
            conversionRate: Math.round(rate),
          };
        });

        setCallVolumeData(callVolume);

        // --- Pipeline prospects ---
        const totalPipeline =
          (statsByStatus.prospect || 0) +
          (statsByStatus.qualifie || 0) +
          (statsByStatus.proposition || 0) +
          (statsByStatus.negociation || 0) +
          (statsByStatus.conclu || 0);

        const stages = [
          { id: 1, key: 'prospect', name: 'Prospects' },
          { id: 2, key: 'qualifie', name: 'Qualification' },
          { id: 3, key: 'proposition', name: 'Proposition' },
          { id: 4, key: 'conclu', name: 'Conclu' },
        ].map((s) => {
          const count = statsByStatus[s.key] || 0;
          const percentage = totalPipeline
            ? Math.round((count / totalPipeline) * 100)
            : 0;
          return {
            id: s.id,
            name: s.name,
            count,
            percentage,
            value: `${count} prospects`,
            conversionRate: percentage,
          };
        });

        setPipelineStages(stages);

        // --- Carte thermique d'engagement (appel par jour / tranche horaire) ---
        const heatmap = Array.from({ length: 7 }, () => Array(6).fill(0));
        calls.forEach((c) => {
          const d = new Date(c.startCallAt);
          const dayIndex = (d.getDay() + 6) % 7; // 0 = Lun ... 6 = Dim
          const hour = d.getHours();
          const slotIndex =
            hour < 4 ? 0 : hour < 8 ? 1 : hour < 12 ? 2 : hour < 16 ? 3 : hour < 20 ? 4 : 5;
          heatmap[dayIndex][slotIndex] += 1;
        });

        // Normalisation simple: plus il y a d'appels, plus la valeur est élevée (cap à 100)
        const maxCell = heatmap.flat().reduce((m, v) => Math.max(m, v), 0) || 1;
        const normalized = heatmap.map((row) =>
          row.map((v) => Math.round((v / maxCell) * 100)),
        );

        setEngagementData(normalized);
        setLastUpdate(new Date());
      } catch (e) {
        console.error(e);
        setError("Impossible de charger les données de la vue d'ensemble.");
      } finally {
        setLoading(false);
      }
    };

    const guard = async () => {
      try {
        const meRes = await apiFetch('/me');
        const me = meRes?.data?.user;
        if (me?.permission !== 'admin') {
          navigate('/call-performance-analytics', { replace: true });
          return;
        }
        await loadData();
      } catch (e) {
        console.error(e);
        navigate('/login', { replace: true });
      }
    };

    guard();
  }, [navigate]);

  const handleStageClick = () => {};

  const handleQuickAction = () => {};

  const formatLastUpdate = () => {
    return lastUpdate?.toLocaleTimeString('fr-FR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <>
      <Helmet>
        <title>Vue d'ensemble des ventes - CRM</title>
        <meta name="description" content="Tableau de bord complet des performances de vente avec suivi des appels, pipeline des prospects et métriques d'engagement des contacts" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumb />
        <FilterPanel />

        <main className="main-content">
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4 md:mb-6">
              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
                  Vue d'ensemble des ventes
                </h1>
                <p className="text-sm md:text-base text-muted-foreground">
                  Tableau de bord complet des performances et métriques clés
                </p>
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 md:gap-4">
                <div className="flex items-center gap-2 px-3 md:px-4 py-2 bg-card rounded-lg border border-border">
                  <Icon name="Clock" size={16} className="text-muted-foreground md:w-5 md:h-5" />
                  <span className="text-xs md:text-sm text-muted-foreground">
                    Mis à jour: {formatLastUpdate()}
                  </span>

                </div>
              </div>
            </div>

            <QuickActions onAction={handleQuickAction} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
            {kpiData?.map((kpi, index) => (
              <MetricCard
                key={index}
                title={kpi?.title}
                value={kpi?.value}
                change={kpi?.change}
                changeType={kpi?.changeType}
                icon={kpi?.icon}
                trend={kpi?.trend}
                loading={loading}
              />
            ))}
            <MetricCard
              title="E-mails restants"
              value={new Intl.NumberFormat('fr-FR').format(mailRemaining)}
              change={
                activeCompany?.name
                  ? `Quota ${activeCompany.name}`
                  : 'Quota entreprise active'
              }
              changeType={
                mailRemaining <= 0
                  ? 'negative'
                  : mailRemaining <= 5
                    ? 'negative'
                    : 'neutral'
              }
              icon="Mail"
              loading={loading || companyCx?.loading}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
            <div className="lg:col-span-2 bg-card rounded-lg p-4 md:p-6 shadow-elevation-2">
              <div className="flex items-center justify-between mb-4 md:mb-6">
                <div>
                  <h2 className="text-lg md:text-xl font-semibold text-foreground mb-1">
                    Volume d'appels et conversions
                  </h2>
                  <p className="text-xs md:text-sm text-muted-foreground">
                    Activité quotidienne avec taux de conversion
                  </p>
                </div>
                <Icon name="BarChart3" size={20} className="text-primary md:w-6 md:h-6" />
              </div>
              <CallVolumeChart data={callVolumeData} loading={loading} />
            </div>

            <div className="bg-card rounded-lg p-4 md:p-6 shadow-elevation-2">
              <div className="flex items-center justify-between mb-4 md:mb-6">
                <div>
                  <h2 className="text-lg md:text-xl font-semibold text-foreground mb-1">
                    Pipeline des prospects
                  </h2>
                  <p className="text-xs md:text-sm text-muted-foreground">
                    Entonnoir de conversion par étape
                  </p>
                </div>
                <Icon name="Filter" size={20} className="text-primary md:w-6 md:h-6" />
              </div>
              <PipelineFunnel 
                stages={pipelineStages} 
                loading={loading}
                onStageClick={handleStageClick}
              />
            </div>
          </div>

          <div className="bg-card rounded-lg p-4 md:p-6 shadow-elevation-2 mb-6 md:mb-8">
            <div className="flex items-center justify-between mb-4 md:mb-6">
              <div>
                <h2 className="text-lg md:text-xl font-semibold text-foreground mb-1">
                  Carte thermique d'engagement
                </h2>
                <p className="text-xs md:text-sm text-muted-foreground">
                  Fréquence d'interaction par jour et heure
                </p>
              </div>
              <Icon name="Activity" size={20} className="text-primary md:w-6 md:h-6" />
            </div>
            <EngagementHeatmap data={engagementData} loading={loading} />
          </div>

          
        </main>
      </div>
    </>
  );
};

export default SalesOverviewDashboard;